import { DownloadSimpleIcon, PlusIcon, SparkleIcon, TrashIcon } from '@phosphor-icons/react';
import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';
import { toast } from 'react-hot-toast';

import { BaseButton } from '../../../components/button/BaseButton';
import { Button } from '../../../components/button/Button';
import { useDownloadDocument } from '../hooks/useDownloadDocument';
import UploadForm from './UploadForm';
import { getDisplayError } from '../../../utils/get-display-error';
import { ConfirmDialog } from '../../../components/dialog/ConfirmDialog';
import { captureException } from '@sentry/react';
import { IDocument } from '../types';
import {
  DOCUMENT_DIALOG_TITLE_CLASS,
  DocumentDialogFooter,
  DocumentEmptyState,
  DocumentFileRow,
} from './DocumentDialogParts';

interface IMinimalFile {
  id: string;
  hash: string;
  name: string;
}

interface IFileUploaderButtonProps {
  buttonText: string;
  title: string;
  file?: IMinimalFile | null;
  initialName?: string;
  disableUpload?: boolean;
  generateDocument?: () => Promise<void>;
  unlinkDocument?: () => Promise<void>;
  onSubmit?: (document: IDocument) => Promise<void>;
  size?: 6 | 8 | 10;
  /** Custom element that opens the dialog instead of the default button. */
  trigger?: React.ReactElement;
}

/**
 * Button + dialog for a single order document: open the file, delete or replace
 * it, or (for system documents) generate it.
 */
export const FileUploaderButton: React.FC<IFileUploaderButtonProps> = (props) => {
  const {
    buttonText,
    title,
    file,
    disableUpload,
    generateDocument,
    initialName,
    onSubmit,
    unlinkDocument,
    size,
    trigger,
  } = props;
  const [open, setOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const { downloadFile, downloadingFiles } = useDownloadDocument();

  const handleSubmit = async (document: IDocument) => {
    if (onSubmit) {
      try {
        await onSubmit(document);
        setOpen(false);
        toast.success('Bestand opgeladen');
      } catch (err) {
        captureException(err);
        console.error(err);
        toast.error('Kon bestand niet opladen: ' + getDisplayError(err));
      }
    }
  };

  const runGenerate = async () => {
    if (!generateDocument) return;
    setIsGenerating(true);
    try {
      await generateDocument();
      toast.success('Document gegenereerd');
    } catch (err) {
      captureException(err);
      console.error(err);
      toast.error('Kon document niet genereren: ' + getDisplayError(err));
    }
    setIsGenerating(false);
  };

  const isLoading = Boolean(file && disableUpload && downloadingFiles.includes(file.id)) || isGenerating;
  const showFooter = disableUpload || (file && generateDocument);

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild disabled={isLoading}>
        {trigger ?? (
          <BaseButton
            color={file ? 'primary' : 'default-outline'}
            size={size}
            iconLeft={
              file || disableUpload ? (
                <DownloadSimpleIcon className="button-icon" />
              ) : (
                <PlusIcon className="button-icon" />
              )
            }
            isLoading={isLoading}
          >
            {buttonText}
          </BaseButton>
        )}
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog-content">
          <Dialog.Title className={DOCUMENT_DIALOG_TITLE_CLASS}>{title}</Dialog.Title>

          {file ? (
            <DocumentFileRow
              name={file.name}
              actions={
                <>
                  <BaseButton
                    size={6}
                    iconLeft={<DownloadSimpleIcon className="h-3 w-3" />}
                    onClick={() => downloadFile(file)}
                    isLoading={downloadingFiles.includes(file.id)}
                  >
                    Openen
                  </BaseButton>
                  {unlinkDocument && (
                    <ConfirmDialog
                      triggerSize={6}
                      triggerColor="danger"
                      triggerTitle="Verwijder document"
                      triggerText={<TrashIcon className="h-3 w-3" />}
                      title="Ben je zeker dat je dit document wilt verwijderen?"
                      submitText="Verwijder document"
                      description="Ben je zeker dat je dit document wilt verwijderen?"
                      onSubmit={async () => {
                        await unlinkDocument();
                      }}
                    />
                  )}
                </>
              }
            />
          ) : (
            <DocumentEmptyState
              text={disableUpload ? 'Dit document is nog niet aangemaakt.' : 'Nog geen document opgeladen.'}
            >
              {generateDocument && (
                <Button
                  color="primary"
                  size={8}
                  iconLeft={<SparkleIcon className="button-icon" />}
                  onTrigger={runGenerate}
                  isLoading={isGenerating}
                >
                  Genereer document
                </Button>
              )}
            </DocumentEmptyState>
          )}

          {!disableUpload && (
            <div className="mt-4">
              <div className="label-text mb-2">{file ? 'Vervangen' : 'Upload'}</div>
              <UploadForm
                onSubmit={handleSubmit}
                initialName={initialName}
                onCancel={() => {
                  setOpen(false);
                }}
              />
            </div>
          )}

          {showFooter && (
            <DocumentDialogFooter
              left={
                file &&
                generateDocument && (
                  <Button
                    size={8}
                    iconLeft={<SparkleIcon className="button-icon" />}
                    onTrigger={runGenerate}
                    isLoading={isGenerating}
                  >
                    Opnieuw genereren
                  </Button>
                )
              }
            >
              {disableUpload && (
                <Dialog.Close asChild disabled={isLoading}>
                  <BaseButton size={8} isLoading={isLoading}>
                    Sluit
                  </BaseButton>
                </Dialog.Close>
              )}
            </DocumentDialogFooter>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
